import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import {
  SERVICE_TIERS,
  TOUCH_UPS,
  MATERIALS,
  PROCESS_IMAGE,
  whatsappLink,
} from "@/lib/business";
import SectionHeader from "@/components/SectionHeader";

export const Services = () => {
  return (
    <div data-testid="services-page" className="bg-[#050505]">
      {/* Page hero */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00]">
            / SERVICES
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
            EVERY JOB.
            <br />
            <span className="text-[#E60000]">EVERY VEHICLE.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#d4d4d8] leading-relaxed">
            Single-stage and basecoat/clearcoat full repaints, panel refinishing,
            touch-ups, and full restoration. We document every step.
          </p>
        </div>
      </section>

      {/* Service tiers detailed */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
            {SERVICE_TIERS.map((t) => (
              <div
                key={t.id}
                className="bg-[#0a0a0a] relative hover:bg-[#111] transition-colors overflow-hidden"
                data-testid={`service-tier-${t.id}`}
              >
                {t.featured && (
                  <span className="absolute top-0 right-0 bg-[#E60000] text-white font-display text-[10px] tracking-widest px-3 py-1 z-10">
                    PREMIUM
                  </span>
                )}
                
                {/* Car image */}
                {t.image && (
                  <div className="h-48 lg:h-56 overflow-hidden bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] relative">
                    <img 
                      src={t.image} 
                      alt={t.title}
                      className="w-full h-full object-cover object-center opacity-90"
                    />
                    {t.vehicleType && (
                      <div className="absolute top-4 left-4 bg-[#FFCC00] text-black font-display text-xs tracking-[0.3em] px-4 py-2">
                        {t.vehicleType}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Content */}
                <div className="p-10">
                  <div
                    className={`font-display text-xs tracking-[0.3em] ${
                      t.accent === "red" ? "text-[#E60000]" : "text-[#FFCC00]"
                    }`}
                  >
                    {t.eta}
                  </div>
                  <h2 className="mt-3 font-display text-3xl md:text-4xl text-white leading-tight">
                    {t.title}
                  </h2>
                  <div className="mt-3 font-display text-3xl text-white">
                    {t.price}
                  </div>
                  <p className="mt-4 text-[#d4d4d8] leading-relaxed">{t.blurb}</p>
                  <ul className="mt-6 space-y-3">
                    {t.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#e4e4e7]">
                        <CheckCircle2
                          className={`w-5 h-5 mt-0.5 shrink-0 ${
                            t.accent === "red" ? "text-[#E60000]" : "text-[#FFCC00]"
                          }`}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-quote-${t.id}`}
                    className="mt-8 inline-flex items-center gap-2 font-display text-xs tracking-widest text-white border-b border-[#FFCC00] pb-1 hover:text-[#FFCC00]"
                  >
                    REQUEST THIS SERVICE <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Touch-ups */}
      <section className="bg-black py-24 border-t border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="PANEL TOUCH-UPS"
            title="HOOD & FENDER REFINISHING."
            description="Real examples from our shop. Final pricing depends on damage, paint type, and color match."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
            {TOUCH_UPS.map((group) => (
              <div key={group.title} className="bg-[#0a0a0a] p-10">
                <h3 className="font-display text-2xl md:text-3xl text-white">
                  {group.title}
                </h3>
                <div className="mt-6 space-y-4">
                  {group.examples.map((ex) => (
                    <div
                      key={ex.vehicle}
                      className="flex items-start justify-between gap-4 border-b border-[#27272A] pb-4"
                    >
                      <div>
                        <div className="font-display text-lg text-white">
                          {ex.vehicle}
                        </div>
                        <div className="text-sm text-[#d4d4d8] mt-1">{ex.note}</div>
                      </div>
                      <div className="font-display text-2xl text-[#FFCC00]">
                        {ex.price}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials + Process image split */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="WHAT GOES INTO EVERY JOB"
            title="PRO-GRADE MATERIALS."
            description="Every vehicle gets commercial-grade paint, primer, and refinishing materials — matched to your spec and applied in a controlled booth."
          />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
            {MATERIALS.map((m, i) => (
              <div
                key={m.name}
                className="bg-[#0a0a0a] p-6 md:p-7 hover:bg-[#111] transition-colors group relative"
                data-testid={`material-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-xs tracking-widest text-[#E60000]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="block h-[1px] w-8 bg-[#E60000]" />
                </div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl text-white tracking-wide">
                  {m.name.toUpperCase()}
                </h3>
                <p className="mt-2 text-sm text-[#d4d4d8] leading-relaxed">
                  {m.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 relative border border-[#27272A] min-h-[320px] overflow-hidden">
            <img
              src={PROCESS_IMAGE}
              alt="Technician spray painting"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/40 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <div className="inline-block bg-[#E60000] text-white font-display text-[10px] tracking-widest px-3 py-1 mb-3">
                IN THE BOOTH
              </div>
              <div className="font-display text-3xl md:text-5xl text-white">
                CONTROLLED. CLEAN. PRECISE.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-[#0a0a0a] border-t border-[#27272A] py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div>
            <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-2">
              READY?
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white">
              LET'S BUILD YOUR <span className="text-[#E60000]">QUOTE</span>.
            </h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="services-cta-quote"
              className="inline-flex items-center gap-3 bg-[#FFCC00] hover:bg-white text-black font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
            >
              REQUEST QUOTE <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-cta-whatsapp"
              className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
            >
              <MessageCircle className="w-4 h-4" /> WHATSAPP
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
